// @flow

import * as React from 'react';
import fastDeepEqual from 'fast-deep-equal';
import { Route, Visitor } from '@ta/lithium.context';

export type GAEventProps = {|
  module: string,
  action: string,
  value?: number,
  location?: number,
  context?: string,
  nonInteraction?: boolean,
|};

export type GARecorderFunction = GAEventProps => void;

const INTERACTION_BUFFER_KEY = 'interaction_buffer';
const BUFFER_TIMEOUT = 1000;

const submit = records => {
  if (records.length === 0) {
    return Promise.resolve();
  }

  const data = new URLSearchParams('?batched=true');
  records.forEach((record, index) => {
    record.forEach((value, key) => {
      if (typeof value !== 'undefined') {
        data.append(`data[${index}][${key}]`, value);
      }
    });
  });

  return fetch('/GARecord', {
    method: 'POST',
    body: data.toString(),
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
      'X-Requested-With': 'XMLHttpRequest',
    },
  }).catch(e => {
    console.error(e);
  });
};

const createUrlSearchParamsFromBufferEntry = (entry: { [string]: mixed }) => {
  const params = new URLSearchParams();
  Object.entries(entry).forEach(([k, v]) => {
    if (k !== 'eventId') {
      params.append(k, (v: any));
    }
  });
  return params;
};

let lock = false;
const resolve = () => {
  // We lock while requests are pending in order to prevent duplicate recording
  // and allow us to only remove events from the buffer once they've been recorded.
  if (!lock) {
    lock = true;
    try {
      const bufferText = localStorage.getItem(INTERACTION_BUFFER_KEY);
      const buffer = bufferText ? JSON.parse(bufferText) : [];
      submit(buffer.map(createUrlSearchParamsFromBufferEntry))
        .then(() => {
          const newBufferText = localStorage.getItem(INTERACTION_BUFFER_KEY);
          const newBuffer = newBufferText ? JSON.parse(newBufferText) : [];
          localStorage.setItem(
            INTERACTION_BUFFER_KEY,
            JSON.stringify(
              newBuffer.filter(item => buffer.every(oldItem => !fastDeepEqual(oldItem, item))),
            ),
          );
        })
        .finally(() => {
          lock = false;
        });
    } catch (e) {
      // Browsers throw if localstorage permissions are denied.
      lock = false;
    }
  } else {
    // These functions are mutually recursive.
    // eslint-disable-next-line no-use-before-define
    triggerPost();
  }
};

let submissionTriggerTimeoutId = null;
const triggerPost = () => {
  if (submissionTriggerTimeoutId !== null) {
    clearTimeout(submissionTriggerTimeoutId);
  }
  submissionTriggerTimeoutId = setTimeout(resolve, BUFFER_TIMEOUT);
};

const addToBuffer = entry => {
  try {
    const existingBufferText = localStorage.getItem(INTERACTION_BUFFER_KEY);
    const existingBuffer = existingBufferText ? JSON.parse(existingBufferText) : [];
    existingBuffer.push(entry);
    localStorage.setItem(INTERACTION_BUFFER_KEY, JSON.stringify(existingBuffer));
  } catch (e) {
    // Browsers will throw if localstorage is full or access is denied.
    return false;
  }
  triggerPost();
  return true;
};

const enqueue = (record: URLSearchParams) => {
  // Safety check time!
  // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
  if (SERVER) {
    if (DEBUG) {
      throw new Error(
        'Attempting to log a GARecord inside of Node: Please move your tracking to a useEffect',
      );
    }
    return;
  }
  const obj = { eventId: Math.random().toString() };
  record.forEach((v, k) => {
    obj[k] = v;
  });
  if (!addToBuffer(obj)) {
    // If localstorage isn't available, we'll do a best-effort submission of the event.
    // We can't use sendBeacon here because GARecord expects an X-Requested-With header.
    submit([record]);
  }
};

const getGassGuess = (path: string): string => {
  try {
    return path.match(/^\/([a-zA-Z0-9_]+)/)?.[1] || 'Home';
  } catch (e) {
    return 'Unknown';
  }
};

const getGaslGuess = (path: string): string | void => {
  try {
    return path.match(/-d([0-9]+)/)?.[1] || path.match(/-g([0-9]+)/)?.[1] || undefined;
  } catch (e) {
    return undefined;
  }
};

const useGARecorder = (): GARecorderFunction => {
  const [{ page, params }, { uid: puid }] = React.useContext(Route);
  const { userId } = React.useContext(Visitor);

  return React.useCallback(
    (props: GAEventProps) => {
      // Tests get slowed down too much by this check. We'll ignore it in that environment
      if (DEBUG && typeof navigator !== 'undefined' && navigator.userAgent !== 'node.js') {
        try {
          throw new Error(
            'Attempted to record GARecord during render: This is a side effect and should be handled via useEffect.',
          );
        } catch (e) {
          if (e.stack && e.stack.includes('renderWithHooks')) {
            console.error(e);
            return;
          }
        }
      }
      // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
      if (SERVER) {
        throw new Error('Attempted to record GARecord on SSR');
      }

      const gaslGuess =
        page === 'Unknown'
          ? // eslint-disable-next-line no-restricted-globals
            getGaslGuess(window.location.pathname)
          : params.detailId || params.geoId || undefined;

      const entry = {
        gac: props.module, // /GARecord calls this 'category'
        gaa: props.action,
        gal: props.context, // /GARecord calls this 'label'
        gav: props.value,
        gani: props.nonInteraction,

        // This is best effort only.
        // It's impossible without knowledge of the specific page to know any better.
        gasl: props.location != null ? props.location : gaslGuess,

        // Obviously these concepts aren't exactly the same, but again, this is best effort.
        // The purpose of this is to identify where the event came from, and page will achieve that.
        // eslint-disable-next-line no-restricted-globals
        gass: page === 'Unknown' ? getGassGuess(window.location.pathname) : page,

        gapu: puid,
        gams: userId !== null,

        // We use this to allow data-duplicate events to be collected.
        eventId: Math.random().toString(),
      };
      if (!addToBuffer(entry)) {
        // If we fail to write to the buffer, the best we can do is race.
        submit([createUrlSearchParamsFromBufferEntry(entry)]);
      }
    },
    [page, params, puid, userId],
  );
};

const useGARecordCallback = (gaEventProps: ?GAEventProps) => {
  const record = useGARecorder();
  const { module, action, context, value, location, nonInteraction } = gaEventProps || {};
  return React.useCallback(() => {
    if (module && action) {
      record({ module, action, context, value, location, nonInteraction });
    }
  }, [record, module, action, context, value, location, nonInteraction]);
};

// This is the rare instance where we actually want different code on the client and server.
// While not normal, the capability to perform this action doesn't exist in a server environment.
// eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
if (!SERVER && typeof requestIdleCallback !== 'undefined') {
  // On the client, we want to check for any left-behind buffered entries.
  requestIdleCallback(triggerPost);
}

export { useGARecordCallback, useGARecorder, enqueue };
export { useInteractionLogger, useSubmitInteractionBufferEffect } from './interactions-service';
